import Element from 'ui/Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import mixin from 'privates/utils/mixin';
import * as members from 'privates/privateMembers';
import {validate} from 'privates/validations/validation';
import {emptyStringIfNil} from 'privates/utils';

/**
 * @class GoogleMap
 * @summary A [Google Map element](https://www.wix.com/support/html5/article/adding-google-maps)
 *  that allows you to display a given location.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.GoogleMap
 * @tagname GoogleMap
 */
class GoogleMap extends Element {

    /**
     * @typedef {external:Object} $w.GoogleMap~MapLocation
     * @syntax {latitude: 37.8, longitude: -122.4, description: "My Humble Home"}
     * @summary An object used by the `location` property that
     *  contains the attributes of a marked map location.
     * @property {external:Number} latitude The latitude of the location. Must be between -90 and 90.
     * @property {external:Number} longitude The longitude of the location. Must be between -180 and 180.
     * @property {external:String} [description] The description of the location.
     * @snippet [GoogleMap-location_get.es6=Get map location info]
     * @see [location](#location)
     */

    /**
     * @member location
     * @label property
     * @syntax
     * get location(): MapLocation
     * set location(value: MapLocation): void
     * @summary Sets or gets the location information of a map's marked location.
     * @description
     *  Setting the `location` property to a [`MapLocation`](#MapLocation)
     *  object moves the map's marked location.
     *
     *  Getting the `location` property returns the [location
     *  information](#MapLocation) of the map's marked location.
     * @snippet [GoogleMap-location_get.es6=Get map location info]
     * @snippet [GoogleMap-location_set.es6=Set map location info]
     * @type {$w.GoogleMap~MapLocation}
     * @memberof $w.GoogleMap
     */
    set location(location) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'location',
            dataToValidate: location,
            shouldCheckMandatoryFields: true,
            compName: this.id
        })) {
            return;
        }
        const description = emptyStringIfNil(location.description);
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setData(compId, {
            latitude: location.latitude,
            longitude: location.longitude,
            addressInfo: description
        });
    }

    get location() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId);
        return {
            latitude: compData.latitude,
            longitude: compData.longitude,
            description: compData.addressInfo
        };
    }

    toJSON() {
        const {id, type, global, rendered, location} = this;
        return {id, type, global, rendered, location};
    }
}

mixin(GoogleMap.prototype, HiddenCollapsedMixin.prototype);

export default GoogleMap;



// WEBPACK FOOTER //
// ./js/modules/ui/GoogleMap.es6